// The live feed's SCOPE RECONCILER — the piece that decides which
// `liveChanges(scope:)` subscriptions are open at any moment.
//
// docs/live-updates.md: the server delivers change notices per SCOPE TOKEN
// (`competition:<id>`, `event:<id>`, `team:<id>`, `person:<id>`), and the
// global feed — catalogues, the rbac_* tables, every whole-kind import notice
// — is delivered "implicitly", meaning it RIDES ALONG on the scopes a client
// actually holds. Hold none and you receive nothing at all.
//
// Pages must therefore say what they are looking at, and the SET of those
// declarations is what the socket must mirror (ui-conventions.md #104, owner
// ruling 2026-08-26: "The SPA should always subscribe on the active pages'
// possible live feed paths … applied globally, reproducibly and using shared
// code"). This module owns exactly that set:
//
//   - every mounted page declares a reactive list of scope tokens
//     (src/composables/useLiveScopes.ts), plus the shell's always-on FLOOR
//     (`person:<viewer>`, the one scope every signed-in user has, which is
//     what keeps the global feed flowing on screens that have no scope of
//     their own);
//   - the union of all of them is the wanted set — deduped, so two pages
//     naming the same event cost one subscription;
//   - reconcile() opens what is newly wanted and closes what no longer is,
//     so leaving a page or changing the scope pills actually releases the
//     subscriptions it was holding.
//
// Kept out of live.ts itself so it is testable without a WebSocket: the
// transport is an interface with exactly one method, and live.ts supplies the
// graphql-ws implementation.
//
// Reconnects are NOT handled here: graphql-ws re-sends the Subscribe message
// for every operation that is still open when a socket comes back (its
// client's per-subscription retry loop), so the set replayed after a drop is
// by construction the CURRENT one — a scope released before the drop has
// already completed and is not replayed. attach() covers the other direction:
// the transport appearing after declarations already exist (boot order).

/** Opens one scope subscription and returns its unsubscribe. */
export interface ScopeTransport {
  open(scope: string): () => void
}

export interface ScopeRegistry {
  /** Register a page's scope list; returns the handle to update/release it. */
  declare(scopes: readonly string[]): number
  update(token: number, scopes: readonly string[]): void
  release(token: number): void
  /** The always-on floor the shell owns (the viewer's own person scope). */
  setFloor(scopes: readonly string[]): void
  /** Install (or replace) the transport — called when the WS client exists. */
  attach(transport: ScopeTransport | null): void
  /** The union every declaration plus the floor asks for, sorted. */
  active(): string[]
  /** The scopes actually open on the transport, sorted. Equals active() once
   *  a transport is attached; empty before that. */
  subscribed(): string[]
}

/** `global` is never subscribed to by name (docs/live-updates.md) — it rides
 *  along on every other scope — and an empty/absent token is not a scope. */
function usable(scope: string | null | undefined): scope is string {
  return typeof scope === 'string' && scope !== '' && scope !== 'global'
}

export function createScopeRegistry(): ScopeRegistry {
  const declarations = new Map<number, readonly string[]>()
  const open = new Map<string, () => void>()
  let floor: readonly string[] = []
  let transport: ScopeTransport | null = null
  let nextToken = 1
  let wanted = new Set<string>()

  function reconcile(): void {
    const next = new Set<string>()
    for (const scope of floor) if (usable(scope)) next.add(scope)
    for (const scopes of declarations.values()) {
      for (const scope of scopes) if (usable(scope)) next.add(scope)
    }
    wanted = next

    // Drop first, then add: a scope that survives the change keeps its
    // existing subscription untouched (no flap on an unrelated declaration).
    for (const [scope, unsubscribe] of [...open]) {
      if (next.has(scope)) continue
      open.delete(scope)
      unsubscribe()
    }
    if (!transport) return
    for (const scope of next) {
      if (open.has(scope)) continue
      open.set(scope, transport.open(scope))
    }
  }

  return {
    declare(scopes) {
      const token = nextToken++
      declarations.set(token, [...scopes])
      reconcile()
      return token
    },
    update(token, scopes) {
      if (!declarations.has(token)) return
      declarations.set(token, [...scopes])
      reconcile()
    },
    release(token) {
      if (!declarations.delete(token)) return
      reconcile()
    },
    setFloor(scopes) {
      floor = [...scopes]
      reconcile()
    },
    attach(next) {
      if (transport === next) return
      // Everything open belongs to the OLD transport — close it there before
      // switching, or the handles leak with the client that owned them.
      for (const [scope, unsubscribe] of [...open]) {
        open.delete(scope)
        unsubscribe()
      }
      transport = next
      reconcile()
    },
    active() {
      return [...wanted].sort()
    },
    subscribed() {
      return [...open.keys()].sort()
    },
  }
}
